import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_PAGE_CODE } from '../../graphql';
import { RESTORE_TINYCMS_PAGE_CODE, UPDATE_TINYCMS_PAGE_CODE } from '../../graphql/mutations';
import { askInk } from './InkConfirm';

const PLAN_LINK = '/settings/billing';
const codeStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 13,
};

/**
 * Content › Pages › a page › Page code: header and footer code for this page
 * only, on top of the site's own (Settings → Analytics & code), with the same
 * rules — an admin changes it, here only (agents can't see or change it), on a
 * paid plan, and every save is a version you can restore. It goes live on
 * save, apart from publishing, and only on the live page (never a preview).
 */
/**
 * Whether this person may run the page's code in previews: an admin, a plan
 * with page code, and code on the page. Shares PageCodeSection's query.
 */
export function usePageCode(pageId: string) {
  const { accountId } = useAccount();
  const { data } = useQuery(GET_TINYCMS_PAGE_CODE, {
    variables: { accountId, pageId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const c = data?.tinycmsPageCode;
  return {
    canRun: Boolean(c?.canManage && c?.customCodeAllowed !== false && (c?.headHtml || c?.bodyHtml)),
  };
}

export function PageCodeSection({ pageId, live }: { pageId: string; live: boolean }) {
  const { accountId } = useAccount();
  const { data, refetch } = useQuery(GET_TINYCMS_PAGE_CODE, {
    variables: { accountId, pageId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [update, { loading: saving }] = useMutation(UPDATE_TINYCMS_PAGE_CODE);
  const [restore] = useMutation(RESTORE_TINYCMS_PAGE_CODE);
  const current = data?.tinycmsPageCode;
  const versions: any[] = data?.tinycmsPageCodeVersions || [];
  const [open, setOpen] = useState(false);
  const [head, setHead] = useState('');
  const [body, setBody] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setHead(current?.headHtml || '');
    setBody(current?.bodyHtml || '');
  }, [current?.headHtml, current?.bodyHtml, current?.version]);

  if (!current) return null;
  const canManage = Boolean(current.canManage);
  const allowed = current.customCodeAllowed !== false;
  const has = Boolean(current.headHtml || current.bodyHtml);
  // Nothing to see for someone who can't add any.
  if (!canManage && !has) return null;
  const dirty = head !== (current.headHtml || '') || body !== (current.bodyHtml || '');
  const editable = canManage && (allowed || has);

  const save = async () => {
    setMessage(null);
    try {
      const { data: d } = await update({
        variables: {
          accountId,
          pageId,
          input: { headHtml: head, bodyHtml: body },
          note: note.trim() || null,
        },
      });
      setNote('');
      await refetch();
      setMessage({
        tone: 'ok',
        text: `Saved as version ${d.updateTinycmsPageCode.version}.${live ? ' Live on this page now.' : ''}`,
      });
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof Error ? err.message : 'Couldn’t save the code',
      });
    }
  };

  const restoreVersion = async (version: number) => {
    const ok = await askInk({
      title: `Restore version ${version}?`,
      body: 'It replaces this page’s code now, and is saved as a new version.',
      confirmLabel: 'Restore',
    });
    if (!ok) return;
    setMessage(null);
    try {
      await restore({ variables: { accountId, pageId, version } });
      await refetch();
      setMessage({ tone: 'ok', text: `Restored version ${version}.` });
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof Error ? err.message : 'Couldn’t restore it',
      });
    }
  };

  return (
    <div className="pg-more">
      <button
        type="button"
        className="pg-more-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? '▾' : '▸'} Page code{has ? ' · on' : ''}
      </button>
      {open ? (
        <div className="pg-checks">
          <p className="pg-note" style={{ margin: 0 }}>
            Code for this page only, added after your site’s own code (Website › Manage › Code &amp;
            tracking). It goes live when you save, without publishing. Previews leave it out unless
            you tick Run page code above the preview. Agents can’t see or change it. Visitors get
            this page as a fresh load each time, so its scripts always start cleanly.
            {!live ? ' This page isn’t live yet, so its code isn’t on your site.' : ''}
          </p>
          {!allowed ? (
            <p className="pg-note" style={{ margin: 0 }}>
              Page code comes with a paid plan
              {has ? '; the code below is kept but not on your site' : ''}.{' '}
              <Link to={PLAN_LINK}>Change plan</Link>
            </p>
          ) : null}
          {!canManage ? (
            <p className="pg-note" style={{ margin: 0 }}>
              Only an account admin can change page code.
            </p>
          ) : null}
          <label className="ink-site-label">
            Header code
            <textarea
              className="ink-site-input"
              rows={5}
              style={codeStyle}
              value={head}
              readOnly={!editable}
              onChange={(e) => setHead(e.target.value)}
              placeholder={'<script type="module" src="https://…/story.js"></script>'}
              spellCheck={false}
            />
          </label>
          <label className="ink-site-label">
            Footer code
            <textarea
              className="ink-site-input"
              rows={5}
              style={codeStyle}
              value={body}
              readOnly={!editable}
              onChange={(e) => setBody(e.target.value)}
              placeholder="<script>…</script>"
              spellCheck={false}
            />
          </label>
          {editable ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <input
                className="ink-site-input"
                style={{ maxWidth: 280 }}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What changed?"
                aria-label="What changed (optional)"
              />
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={!dirty || saving}
                onClick={() => void save()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              {dirty ? (
                <button
                  type="button"
                  className="ink-btn"
                  onClick={() => {
                    setHead(current.headHtml || '');
                    setBody(current.bodyHtml || '');
                  }}
                >
                  Discard changes
                </button>
              ) : null}
            </div>
          ) : null}
          {message ? (
            <p
              role={message.tone === 'error' ? 'alert' : undefined}
              style={{
                margin: 0,
                fontSize: 13,
                color: message.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-ink2)',
              }}
            >
              {message.text}
            </p>
          ) : null}
          {versions.length ? (
            <div>
              <div className="ink-site-label">History</div>
              <ul
                style={{
                  listStyle: 'none',
                  margin: '6px 0 0',
                  padding: 0,
                  display: 'grid',
                  gap: 6,
                }}
              >
                {versions.map((v) => (
                  <li
                    key={v.version}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      alignItems: 'center',
                      fontSize: 13,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>v{v.version}</span>
                    <span style={{ color: 'var(--ink-ink2)', flex: 1, minWidth: 0 }}>
                      {new Date(v.changedAt).toLocaleString()} · {v.changedBy || 'unknown'}
                      {v.note ? ` · ${v.note}` : ''}
                    </span>
                    {v.version === current.version ? (
                      <span style={{ color: 'var(--ink-ink3)', fontSize: 12 }}>current</span>
                    ) : canManage ? (
                      <button
                        type="button"
                        className="ink-btn"
                        onClick={() => void restoreVersion(v.version)}
                      >
                        Restore
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
